import { AnyWindowEntity, WindowEntity, WindowLayout } from 'types/windows';
import { WindowWithApplicationState } from '../../../types/appState';

import { FileSkeleton, type Note } from '../../../types';
import type { BaseEntityType } from '../../../types/entities';

import { iconService } from '../../../services/iconService';
import { useSpaceStore } from '../../../stores/spaceStore';
import { useSpaceCameraStore } from '../../../stores/spaceCameraStore';
import { entityRegistry } from '../../../stores/entityRegistry';
import { useAppRegistryStore } from '../../../stores/appRegistryStore';
import { useEntityById } from '../../../stores/entityRouter';
import useUserStore from '../../../stores/userStore';
import { readDockPreferencesFromProfile } from '../../../constants/dock';
import { useMemo } from 'react';

/**
 * Centralized helper to extract entityId from a window.
 * Priority 1: Root-level entityId — the canonical reference (always use this).
 * Priority 2: Legacy applicationState per-app fields — fallback for apps that have not yet
 *   been migrated to set window.entityId directly.
 *
 * Migration status:
 *   - DocsApp: migrated (sets entityId on open/close)
 *   - NotesMarkdownApp: pending — still uses applicationState.notes
 *   - PDFiumApp: pending — may use applicationState.pdfium.entityId
 */
export function getWindowEntityId(window: WindowEntity): string | undefined {
  if (window.entityId) return window.entityId;

  const legacyId =
    window.applicationState?.docs?.activeDocId ||
    window.applicationState?.pdfium?.entityId ||
    window.applicationState?.notes?.activeNoteId ||
    window.applicationState?.notes?.selectedNoteId ||
    window.applicationState?.entityView?.entityId;

  if (legacyId && process.env.NODE_ENV === 'development') {
    // This log helps identify apps that still need to be migrated to set window.entityId
    console.debug('[getWindowEntityId] Using legacy applicationState fallback — migrate app to set window.entityId directly', {
      windowId: (window as any).id,
      appType: window.appType,
      legacyId,
    });
  }

  return legacyId;
}

/**
 * Centralized helper to extract entityLlmId from a window
 */
export function getWindowEntityLlmId(window: WindowEntity): string | undefined {
  return window.entityLlmId;
}


export const generateWindowId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 8);
  return `window-${timestamp}-${random}`;
};

export const getAppTitle = (appTypeOrWindow: string | WindowWithApplicationState): string => {
  // If an object is passed, it might be a window with application state
  if (typeof appTypeOrWindow === 'object') {
    const window = appTypeOrWindow;
    
    // Check for docs application state
    if (window.applicationState?.docs) {
      const docsState = window.applicationState.docs;
      
      // If we're editing a document, prefer entity/recentDocs name over transient CRDT name to avoid duplication
      if (docsState.currentState === 'editing') {
        if (docsState.recentDocs && docsState.activeDocId) {
          const activeDoc = docsState.recentDocs.find((doc) => doc.docId === docsState.activeDocId);
          if (activeDoc?.name) return activeDoc.name;
        }
        if (docsState.currentDocName) return docsState.currentDocName;
      }
      
      // Default docs title
      return 'Documents';
    }
    
    // For other app types, get the app type from the window
    const appType = window.appType || '';
    // Use the appType string to lookup a title (avoid recursion)
    return getAppTitleByType(appType);
  }
  
  // String-based app type lookup
  return getAppTitleByType(appTypeOrWindow);
};

// Helper function to get app title by type string
export const getAppTitleByType = (appType: string): string => {
  // The app registry is the source of truth for installed app display names.
  // Consult it first so dynamically-registered apps not enumerated in the switch
  // below resolve to their real name instead of falling through to "Window".
  const registryName = useAppRegistryStore.getState().apps[appType]?.skeleton?.name;
  if (registryName) return registryName;

  switch (appType) {
    case 'chat':
      return 'Chat';
    case 'system':
    case 'entityBrowser':
      return 'Entity Browser';
    case 'entityBrowser2':
      return 'Drive';
    case 'console':
      return 'Console';
    case 'pdf':
      return 'PDF Viewer';
    case 'pdfium':
      return 'PDFium Viewer';
    case 'notes':
      return 'Notes';
    case 'docs':
      return 'Documents';
    case 'browser':
      return 'Browser';
    case 'webdoc':
      return 'Web Document';
    case 'settings':
      return 'Settings';
    case 'imagestudio':
    case 'mediaviewer':
      return 'Image Studio';
    case 'socialstudio':
      return 'Social Studio';
    case 'bank':
      return 'Bank';
    case 'bookkeeper':
      return 'Bookkeeper';
    case 'disco':
      return 'Disco - Data Analysis';
    case 'prompt':
      return 'Prompt';
    case 'google-drive-file':
      return 'Google Drive File';
    case 'mail':
      return 'Mail';
    case 'contacts':
      return 'Contacts';
    case 'automations':
      return 'Automations';
    case 'notifications':
      return 'Notifications';
    case 'tasks':
      return 'Tasks';
    case 'goals':
      return 'Goals';
    case 'app-builder':
      return 'App Builder';
    case 'admin-dashboard':
      return 'Admin';
    case 'prospect':
      return 'Prospecting';
    case 'sales-integrations':
      return 'Sales Integrations';
    case 'pong':
      return 'Pong';
    case 'rapids':
      return 'Rapids';
    default:
      return 'Window';
  }
};

export const getAppTypeByExt = (ext: string): string | null => {
  switch (ext.toLowerCase()) {
    case '.pdf':
      return 'pdfium';
    case '.txt':
    case '.md':
    case '.mmd':
    case '.log': // Common text file
      return 'file-viewer'; // ✅ Text files in readonly viewer
    case '.jpg':
    case '.jpeg':
    case '.png':
    case '.gif':
    case '.webp':
    case '.bmp':
      return 'imagestudio';
    case '.mp4':
    case '.webm':
    case '.mov':
    case '.avi':
    case '.mkv':
      return 'imagestudio'; // Core media app for video
    // ✅ Code files in readonly viewer
    case '.py':
    case '.js':
    case '.ts':
    case '.tsx':
    case '.jsx':
    case '.java':
    case '.cpp':
    case '.c':
    case '.h':
    case '.cs':
    case '.go':
    case '.rs':
    case '.php':
    case '.rb':
    case '.swift':
    case '.html':
    case '.css':
    case '.scss':
    case '.sass':
    case '.less':
    case '.xml':
    case '.yaml':
    case '.yml':
    case '.toml':
    case '.ini':
    case '.cfg':
    case '.conf':
    case '.sh':
    case '.bash':
    case '.zsh':
    case '.fish':
    case '.sql':
    case '.r':
    case '.lua':
    case '.perl':
    case '.dockerfile':
    case '.makefile':
    case '.cmake':
    case '.vim':
      return 'file-viewer';
    // ✅ Data files in readonly viewer
    case '.json':
    case '.csv':
      return 'file-viewer';
    // ✅ Audio files in readonly viewer
    case '.mp3':
    case '.wav':
    case '.ogg':
    case '.flac':
    case '.aac':
    case '.m4a':
      return 'file-viewer'; // Show metadata/unsupported message
    // ✅ Archive files in readonly viewer
    case '.zip':
    case '.tar':
    case '.gz':
    case '.rar':
    case '.7z':
      return 'file-viewer'; // Show metadata/unsupported message
    // ✅ Spreadsheet files to core app
    case '.xlsx':
    case '.xls':
    case '.ods':
      return 'sheets-v2';
    // ✅ Presentation files to core app
    case '.pptx':
    case '.ppt':
    case '.odp':
      return 'presentations-v2';
    default:
      return null;
  }
};

export const getAppTypeByEntity = (entity: BaseEntityType): string | null => {
  console.log('[WINDOW_UTILS] getAppTypeByEntity called:', { entityType: entity.entityType, name: entity.name });

  // VideoStudioApp — VideoProject is the routable timeline container
  if (entity.entityType === 'VideoProject') {
    console.log('[WINDOW_UTILS] Matched VideoProject, returning videostudio');
    return 'videostudio';
  }

  // DiagramApp — Diagram opens in DiagramApp window
  if (entity.entityType === 'Diagram') {
    console.log('[WINDOW_UTILS] Matched Diagram, returning diagram');
    return 'diagram';
  }

  // ChartApp — Chart opens in ChartApp window
  if (entity.entityType === 'Chart') {
    console.log('[WINDOW_UTILS] Matched Chart, returning chart');
    return 'chart';
  }

  // Rule entities open in RulesApp
  if (entity.entityType === 'Rule') {
    console.log('[WINDOW_UTILS] Matched Rule entity type, returning rules');
    return 'rules';
  }

  // Handle Mail entities - these should open in EntityViewApp for entity-specific views
  if (entity.entityType === 'Mail.Draft' || 
      entity.entityType === 'Mail.Thread' || 
      entity.entityType === 'Mail.Message' ||
      entity.entityType === 'Mail.Contact' ||
      entity.entityType === 'Mail.Audience' ||
      entity.entityType === 'Mail.Broadcast') {
    console.log('[WINDOW_UTILS] Matched Mail entity type, returning entity-view', { entityType: entity.entityType });
    return 'entity-view';
  }
  
  // Handle hierarchical PDF types directly
  if (entity.entityType === 'PDF' || 
      entity.entityType === 'File.Document.PDF' || 
      entity.entityType?.startsWith('File.Document.PDF')) {
    console.log('[WINDOW_UTILS] Matched PDF type, returning pdfium');
    return 'pdfium';
  }

  // Handle hierarchical image types directly  
  if (entity.entityType?.startsWith('File.Image')) {
    console.log('[WINDOW_UTILS] Matched image type, returning imagestudio');
    return 'imagestudio';
  }

  // Handle hierarchical video types directly  
  if (entity.entityType?.startsWith('File.Video')) {
    console.log('[WINDOW_UTILS] Matched video type, returning imagestudio');
    return 'imagestudio';
  }

  // Handle data files (CSV, JSON, XML, etc.)
  if (entity.entityType?.startsWith('File.Data')) {
    console.log('[WINDOW_UTILS] Matched data type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer with table/JSON display
  }

  // Handle code files
  if (entity.entityType?.startsWith('File.Code')) {
    console.log('[WINDOW_UTILS] Matched code type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer with syntax highlighting
  }

  // Handle text/markdown documents
  if (entity.entityType?.startsWith('File.Document.Text') || 
      entity.entityType?.startsWith('File.Document.Markdown')) {
    console.log('[WINDOW_UTILS] Matched text document type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer
  }

  // Handle spreadsheet files
  if (entity.entityType?.startsWith('File.Spreadsheet')) {
    console.log('[WINDOW_UTILS] Matched spreadsheet type, returning sheets-v2');
    return 'sheets-v2'; // Core spreadsheet app
  }

  // Handle audio/video files
  if (entity.entityType?.startsWith('File.Audio')) {
    console.log('[WINDOW_UTILS] Matched audio type, returning file-viewer');
    return 'file-viewer'; // Show in readonly viewer
  }

  if (entity.entityType?.startsWith('File.Video')) {
    console.log('[WINDOW_UTILS] Matched video type, returning imagestudio');
    return 'imagestudio'; // Core media app
  }

  // Handle archive files
  if (entity.entityType?.startsWith('File.Archive')) {
    console.log('[WINDOW_UTILS] Matched archive type, returning file-viewer');
    return 'file-viewer'; // Show in readonly viewer
  }

  // Legacy PDF detection by MIME type
  if ((entity?.skeleton as FileSkeleton)?.['@type'] === 'File' && 
      (entity?.skeleton as FileSkeleton)?.mimeType === 'application/pdf') {
    console.log('[WINDOW_UTILS] Matched legacy PDF by MIME type, returning pdfium');
    return 'pdfium';
  }

  // Handle any file type (legacy "File" or hierarchical "File.*")
  if (entity.entityType === 'File' || entity.entityType?.startsWith('File.')) {
    // Use skeleton.fileName first, fallback to entity.name
    const fileName = ((entity.skeleton as any)?.fileName || entity.name || '').toLowerCase();
    const ext = fileName.substring(fileName.lastIndexOf('.'));
    const appType = getAppTypeByExt(ext);
    console.log('[WINDOW_UTILS] File type detected:', { fileName, ext, appType });
    return appType;
  }

  console.log('[WINDOW_UTILS] No app type matched, returning null');
  return null;
};

export const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const getPreviewText = (note: Note) => {
  try {
    const content = note.skeleton?.content;
    if (!content) {
      return 'Empty Note';
    }

    if (typeof content === 'string') {
      return stripHtml(content).substring(0, 50) || 'Empty Note';
    }

    return 'Empty Note';
  } catch (e) {
    return 'Error loading note';
  }
};

const normalizeDisplayName = (name?: string) => {
  if (!name) return '';
  const trimmed = name.trim();
  // Only collapse duplicated file extensions (e.g., "file.pdffile.pdf"), not arbitrary text
  const len = trimmed.length;
  if (len > 1 && len % 2 === 0) {
    const half = len / 2;
    const first = trimmed.slice(0, half);
    const second = trimmed.slice(half);
    // Only collapse if it looks like a file extension duplication (contains a dot)
    if (first === second && first.includes('.')) return first;
  }
  return trimmed;
};

export const getEntityDisplayName = (entity: BaseEntityType | null | undefined) => {
  if (!entity) return 'Untitled';
  // ✅ FIX: Notes have entity.name just like all other entities - no special handling needed!
  if (entity.entityType === 'WebDocument') {
    return normalizeDisplayName((entity as any).skeleton.url);
  } else if (entity.entityType === 'Space') {
    return normalizeDisplayName(entity.name || `space_${entity._id}`);
  } else if (entity.entityType === 'File' || entity.entityType?.startsWith('File.')) {
    // entity.name is the canonical display name — updated by every rename operation.
    // skeleton.fileName is the original upload filename and is NOT updated on rename.
    // We must prefer entity.name so renames reflect immediately.
    // No normalizeDisplayName() here — filenames must preserve exact case and extension.
    return entity.name || (entity.skeleton as any)?.fileName || 'Untitled';
  } else {
    // Default: use entity.name for all entity types including Note, Doc, Group, etc.
    return normalizeDisplayName(entity.name);
  }
};

export const getEntityDisplayIcon = (entity: BaseEntityType) => {
  // Try to get icon from iconService first
  const globalIcon = iconService.getGlobalEntityIcon(entity.entityType);
  if (globalIcon && globalIcon.emoji) {
    return globalIcon.emoji;
  }
  
  // Fallback to hardcoded icons for backward compatibility
  const iconMap = {
    Space: '🃏',  // Cards emoji as fallback
    Group: '📁',
    Note: '📝',
    File: '📄',
    WebDocument: '🌐',
    Doc: '📄',
    Bank: '🏦',
  };
  
  return iconMap[entity.entityType as keyof typeof iconMap] || '⬜️'; // Default to file icon
};

export interface DockInsets {
  bottom: number;
  left: number;
  right: number;
}

const ZERO_DOCK_INSETS: DockInsets = { bottom: 0, left: 0, right: 0 };

/**
 * Space permanently reserved by the desktop dock along whichever screen edge
 * it's docked to. Maximize / edge-extend / snap all subtract this so windows
 * never sit underneath the dock.
 *
 * Mirrors native macOS: an "Always Show" dock permanently reserves space, while
 * a "Show on Hover" dock is a transient overlay that floats ABOVE window
 * content when revealed and never reserves layout space — so this
 * deliberately ignores the dock's momentary hover-shown/hidden DOM state and
 * decides purely from the persisted visibility preference. (A CSS `transform`
 * on the inner dock element for the hide animation doesn't affect the outer
 * holder's layout box anyway, so measuring the DOM mid-transition would be
 * unreliable regardless.)
 *
 * Returns all-zero when there is no persistent dock in this layout (web
 * spatial/canvas, focused mode) or the dock hasn't mounted yet.
 */
export const getDockInsets = (): DockInsets => {
  const dock = document.querySelector('[data-minimize-target="dock"]') as HTMLElement | null;
  if (!dock) return ZERO_DOCK_INSETS;

  const { visibility, edge } = readDockPreferencesFromProfile(useUserStore.getState().profile);
  if (visibility === 'hover') return ZERO_DOCK_INSETS;

  const rect = dock.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return ZERO_DOCK_INSETS;

  if (edge === 'left') return { ...ZERO_DOCK_INSETS, left: Math.max(0, rect.right) };
  if (edge === 'right') return { ...ZERO_DOCK_INSETS, right: Math.max(0, window.innerWidth - rect.left) };
  return { ...ZERO_DOCK_INSETS, bottom: Math.max(0, window.innerHeight - rect.top) };
};

export const getRandomWindowPositionShift = () => {
  const numbers = [-30, -20, -10, 10, 20, 30];
  return numbers[Math.floor(Math.random() * numbers.length)];
};

/**
 * Return a screen-proportional default size for new windows.
 * Fills ~58% of viewport width and ~68% of viewport height, clamped to
 * reasonable bounds so windows are comfortably sized on any screen.
 *
 * Bounds:
 *   width  — 58 % of viewport, clamped to [760, 1280]
 *   height — 68 % of viewport, clamped to [580, 960]
 */
export const getDefaultWindowSize = (): { width: number; height: number } => {
  const w = Math.round(Math.min(Math.max(window.innerWidth  * 0.58, 760), 1280));
  const h = Math.round(Math.min(Math.max(window.innerHeight * 0.68, 580), 960));
  return { width: w, height: h };
};

export const getWindowSizeAndPosition = () => {
  const size = getDefaultWindowSize();
  const position = {
    x: window.innerWidth / 2 - size.width / 2 + getRandomWindowPositionShift(),
    y: window.innerHeight / 2 - size.height / 2 + getRandomWindowPositionShift(),
  };

  return { size, position };
};

/**
 * Return a window size that fills a comfortable fraction of the viewport —
 * used when opening a first-run entity window (e.g. template installs from the
 * intake hero) so the window feels substantial without covering the entire screen.
 *
 * Bounds:
 *   width  — 65 % of viewport, clamped to [680, 1200]
 *   height — 72 % of viewport, clamped to [540, 920]
 */
export const getGenerousWindowSize = (): { width: number; height: number } => {
  const w = Math.round(Math.min(Math.max(window.innerWidth  * 0.65, 680), 1200));
  const h = Math.round(Math.min(Math.max(window.innerHeight * 0.72, 540), 920));
  return { width: w, height: h };
};

/**
 * Return a portrait, document-shaped default size for windows whose content
 * is a page (e.g. newly created Docs) — taller than wide and filling more of
 * the viewport than the generic default, so the page reads naturally instead
 * of looking like a wide, mostly-empty landscape pane.
 *
 * Bounds:
 *   width  — 46 % of viewport, clamped to [720, 880]
 *   height — 86 % of viewport, clamped to [760, 1160]
 *
 * Height is additionally floored at `width + 80` so the window always reads
 * as portrait-oriented, even on short/ultra-wide viewports.
 */
export const getDocumentWindowSize = (): { width: number; height: number } => {
  const width = Math.round(Math.min(Math.max(window.innerWidth * 0.46, 720), 880));
  const height = Math.round(Math.min(Math.max(window.innerHeight * 0.86, 760), 1160));
  return { width, height: Math.max(height, width + 80) };
};

/**
 * Compute the spawn position for a NEW window so it lands centered in the user's
 * current viewport.
 *
 * In `spatialCanvas` mode window positions are WORLD coordinates under a pan/zoom
 * camera, so the screen-centred default would land at an unpredictable world point
 * (often off-screen). Here we convert the viewport centre into world space using the
 * per-user camera. All other layouts use plain screen coordinates.
 */
export const getNewWindowSpawnPosition = (
  size: { width: number; height: number },
): { x: number; y: number } => {
  const screenCentered = {
    x: Math.round(window.innerWidth / 2 - size.width / 2 + getRandomWindowPositionShift()),
    y: Math.round(window.innerHeight / 2 - size.height / 2 + getRandomWindowPositionShift()),
  };

  try {
    const store = useSpaceStore.getState();
    const spaceId = store.activeSpace?.id;
    const viewMode = store.activeSpace?.settings?.viewMode;
    if (viewMode !== 'spatialCanvas' || !spaceId) return screenCentered;

    const camera = useSpaceCameraStore.getState().getCamera(spaceId);
    return {
      x: Math.round((window.innerWidth / 2 - camera.pan.x) / camera.zoom - size.width / 2),
      y: Math.round((window.innerHeight / 2 - camera.pan.y) / camera.zoom - size.height / 2),
    };
  } catch {
    return screenCentered;
  }
};

/**
 * Maps a Space viewMode to a layout family key used for per-mode geometry snapshots.
 * Returns null for modes that don't use spatial window geometry (focused, home, canvas, gui2).
 */
export const layoutFamilyOf = (viewMode?: string): 'spatial' | 'spatialCanvas' | null => {
  if (viewMode === 'spatial') return 'spatial';
  if (viewMode === 'spatialCanvas') return 'spatialCanvas';
  return null;
};

/**
 * Compute a non-overlapping grid arrangement for windows that have no saved
 * layout for the given family. Returns a map of windowId → WindowLayout.
 *
 * `spatial` — screen-space coordinates (zoom = 1). Windows are arranged in a
 * left-to-right, top-to-bottom grid that fills the visible area.
 *
 * `spatialCanvas` — world-space coordinates. The current camera (pan + zoom)
 * is read from `spaceCameraStore` so the grid appears centred in the visible
 * canvas viewport. Requires `spaceId` to be passed; without it falls back to
 * the screen-space layout.
 */
export const computeCascadeLayout = (
  windowIds: string[],
  opts?: { topInset?: number; family?: 'spatial' | 'spatialCanvas'; spaceId?: string },
): Record<string, WindowLayout> => {
  const family = opts?.family ?? 'spatial';

  const topInset = opts?.topInset ?? (() => {
    try {
      const bar = document.querySelector('.river-systembar') as HTMLElement | null;
      return bar ? Math.round(bar.getBoundingClientRect().bottom) : 0;
    } catch { return 0; }
  })();

  const DEFAULT_WIDTH = 900;
  const DEFAULT_HEIGHT = 680;
  const GAP = 24;

  const vw = typeof window !== 'undefined' ? window.innerWidth  : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;

  const usableW = vw;
  const usableH = vh - topInset;

  // How many columns fit in the usable width?
  const cols = Math.max(1, Math.floor((usableW + GAP) / (DEFAULT_WIDTH + GAP)));

  // Grid origin in screen space — centred horizontally, 60 px below the chrome.
  const totalGridW = cols * DEFAULT_WIDTH + (cols - 1) * GAP;
  const screenOriginX = Math.max(GAP, Math.round((usableW - totalGridW) / 2));
  const screenOriginY = topInset + 60;

  // Camera state for spatialCanvas: screen coords → world coords.
  // screen = world * zoom + pan  →  world = (screen - pan) / zoom
  let panX = 0;
  let panY = 0;
  let zoom = 1;

  if (family === 'spatialCanvas' && opts?.spaceId) {
    try {
      const { spaceCameraStore } = require('../../stores/spaceCameraStore');
      const cam = spaceCameraStore.getCamera(opts.spaceId);
      panX = cam.pan?.x ?? 0;
      panY = cam.pan?.y ?? 0;
      zoom = cam.zoom ?? 1;
      if (!zoom || zoom <= 0) zoom = 1;
    } catch { /* fall back to screen-space (zoom = 1, pan = 0) */ }
  }

  // Dimensions in world units (for canvas mode zoom ≠ 1 makes windows physically
  // smaller in world space so they appear at the correct screen size).
  const worldW   = Math.round(DEFAULT_WIDTH  / zoom);
  const worldH   = Math.round(DEFAULT_HEIGHT / zoom);
  const worldGap = Math.round(GAP / zoom);

  // Origin in world space.
  const originX = (screenOriginX - panX) / zoom;
  const originY = (screenOriginY - panY) / zoom;

  // How many rows fit on screen.
  const maxRows = Math.max(1, Math.floor((usableH - 60 + GAP) / (DEFAULT_HEIGHT + GAP)));

  const result: Record<string, WindowLayout> = {};

  windowIds.forEach((id, i) => {
    // After cols × maxRows windows, start a second "page" slightly offset.
    const page    = Math.floor(i / (cols * maxRows));
    const pageIdx = i % (cols * maxRows);
    const col     = pageIdx % cols;
    const row     = Math.floor(pageIdx / cols);

    const x = originX + col * (worldW + worldGap) + page * Math.round(worldW * 0.25);
    const y = originY + row * (worldH + worldGap) + page * Math.round(worldH * 0.25);

    result[id] = {
      position: { x: Math.round(x), y: Math.round(y) },
      size: { width: worldW, height: worldH },
      zIndex: i + 1,
    };
  });

  return result;
};

export const fileAndGroupFilter = (entity: BaseEntityType) =>
  (entity.entityType === 'File' || entity.entityType?.startsWith('File.') || entity.entityType === 'Group') && !entity.isSystemGroup;

export const getWindowTitle = (window: WindowEntity): string => {
  if (!window.appType) return window.title || 'Untitled';

  const windowAny = window as any;

  let currentEntity = null;
  if (window.entityLlmId) {
    currentEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find((e: any) => e.llmId === window.entityLlmId);
  }
  if (!currentEntity) {
    currentEntity = windowAny.entity;
  }

  // ── Title priority ────────────────────────────────────────────────────────
  // `window.title` is authoritative: it is CRDT-persisted and kept in sync with
  // the entity name by `subscribeToChanges`. `currentEntity.name` (from the
  // in-memory registry) may be stale when loaded from CRDT before the REST
  // fetch for the entity has completed. Prefer a custom (non-canonical)
  // `window.title` over entity name, mirroring `getWindowTitleWithEntity`.
  const canonicalTitle = getAppTitleByType(window.appType);
  const autoGeneratedTitle = window.appType.charAt(0).toUpperCase() + window.appType.slice(1);
  const hasCustomWindowTitle = !!(
    window.title &&
    window.title !== canonicalTitle &&
    window.title !== autoGeneratedTitle &&
    window.title !== 'Untitled Document' &&
    window.title !== 'Documents'
  );

  const windowWithState = window as WindowWithApplicationState;
  if (window.appType === 'docs' && windowWithState.applicationState?.docs) {
    const docsState = windowWithState.applicationState.docs;

    if (docsState.currentState === 'editing' && docsState.activeDocId) {
      if (hasCustomWindowTitle) return window.title!;
      if (currentEntity?.name) return currentEntity.name;
      const entityByDocId = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === docsState.activeDocId || e.id === docsState.activeDocId
      );
      if (entityByDocId?.name) return entityByDocId.name;
      return 'Untitled Document';
    }

    if (docsState.currentState === 'overview') return 'Documents';
  }

  if (hasCustomWindowTitle) return window.title!;
  if (currentEntity?.name) return currentEntity.name;
  return canonicalTitle;
};

/**
 * Resolve a window title using a pre-fetched entity — avoids the expensive
 * entityRegistry.dumpAll().flatMap().find() scan that getWindowTitle does.
 * Call this from useWindowTitle; for one-off non-hook usage keep getWindowTitle.
 */
export const getWindowTitleWithEntity = (
  win: WindowEntity | null | undefined,
  entity: BaseEntityType | null | undefined,
): string => {
  if (!win) return 'Untitled';
  if (!win.appType) return win.title || 'Untitled';

  // ── Title priority order ─────────────────────────────────────────────────
  //
  // `win.title` is the authoritative stored title. It is kept in sync with the
  // entity name by the `subscribeToChanges` listener in spaceStore and is
  // persisted to CRDT/IDB on every rename. `entity.name` (from useEntityById)
  // may be stale from the entity-by-id IDB until the REST fetch completes.
  //
  // We therefore prefer a custom (non-canonical) `win.title` over `entity.name`
  // so refreshing always shows the correct title immediately. `entity.name` is
  // still used as a fallback when `win.title` is missing or canonical.

  const canonicalTitle = getAppTitleByType(win.appType);
  const autoGeneratedTitle = win.appType.charAt(0).toUpperCase() + win.appType.slice(1);
  const hasCustomWindowTitle = !!(
    win.title &&
    win.title !== canonicalTitle &&
    win.title !== autoGeneratedTitle &&
    win.title !== 'Untitled Document' &&
    win.title !== 'Documents'
  );

  // Docs: applicationState.docs lets us show "Documents" on the overview screen.
  const windowWithState = win as WindowWithApplicationState;
  if (win.appType === 'docs' && windowWithState.applicationState?.docs) {
    const docsState = windowWithState.applicationState.docs;
    if (docsState.currentState === 'editing' && docsState.activeDocId) {
      // Prefer the CRDT-persisted win.title when it's a real (custom) name.
      // Fall back to entity.name (may arrive async from REST) and then the
      // default "Untitled Document".
      if (hasCustomWindowTitle) return win.title!;
      if (entity?.name) return entity.name;
      return 'Untitled Document';
    }
    if (docsState.currentState === 'overview') return 'Documents';
  }

  // For all other entity-backed windows: prefer custom win.title, then entity.name.
  if (hasCustomWindowTitle) return win.title!;
  if (entity?.name) return entity.name;

  // Static / app-type title
  if (win.title && win.title !== canonicalTitle && win.title !== autoGeneratedTitle) {
    return win.title;
  }
  return canonicalTitle;
};

/**
 * React hook: resolve the display title for a window.
 *
 * Uses useEntityById for an O(1) indexed lookup instead of the expensive
 * entityRegistry.dumpAll().flatMap().find() used by getWindowTitle().
 * Subscribes only to this window's slice + its entity — other windows
 * updating never trigger a re-render here.
 */
export function useWindowTitle(windowId: string): string {
  const win = useSpaceStore((s) => s.windows[windowId]);
  const entity = useEntityById(win?.entityId);
  return useMemo(
    () => getWindowTitleWithEntity(win, entity ?? null),
    // Key inputs that can change the title:
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [win?.title, win?.appType, win?.entityId, (win?.applicationState as any)?.docs?.currentState, (win?.applicationState as any)?.docs?.activeDocId, entity?.name],
  );
}

export const getMentionDisplayName = (entity: AnyWindowEntity): string => {
  if (!entity) return 'Unknown';
  
  // For document windows, use the same logic as getWindowTitle to get the current document name
  const entityAny = entity as any;
  if (entityAny.appType === 'docs' && entityAny.applicationState?.docs) {
    const docsState = entityAny.applicationState.docs;
    
    // If we're editing a document, use its current name
    if (docsState.currentState === 'editing' && docsState.currentDocName) {
      return docsState.currentDocName;
    }
    
    // If we have an activeDocId but no currentDocName, try to find it in recentDocs
    if (docsState.activeDocId && docsState.recentDocs) {
      const activeDoc = docsState.recentDocs.find((doc: any) => doc.docId === docsState.activeDocId);
      if (activeDoc && activeDoc.name) {
        return activeDoc.name;
      }
    }
  }
  
  // For other entity types, use the existing logic
  if ('title' in entity && typeof entity.title === 'string') {
    return entity.title || 'Untitled';
  }
  
  if ('name' in entity && typeof entity.name === 'string') {
    return entity.name;
  }

  return entity.id || 'Unknown';
};

/**
 * Focus an existing window of the given appType, or create a new one if none exists.
 * spaceStore.bringWindowToFront raises both the active window and the spatialUIStore
 * z-order internally.
 */
export async function focusOrCreateAppWindow(
  appType: string,
  createConfig: {
    appType: string;
    title?: string;
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    [key: string]: unknown;
  }
): Promise<void> {
  const { windows, actions, bringWindowToFront } = useSpaceStore.getState();
  const existing = Object.values(windows).find(w => w.appType === appType);
  if (existing) {
    bringWindowToFront(existing.id);
    return;
  }
  const windowId = await actions.createWindow(createConfig);
  actions.setActiveWindow(windowId);
}